import {
  Long,
  OrderData,
  OrderDataImpl,
  OrderIntent,
  OrderState,
  OrderType,
  RateSideType,
  StrategyType,
} from "shared/cubiq-model-multi-asset";
import { SiblingRelationship } from "shared/cubiq-services/lookup/model/impl/SiblingRelationshipManager";

export enum RungPosition {
  LOWEST,
  MID,
  HIGHEST,
}

export class GammaUtils {
  /**
   * Givne an array of Orders
   * return a map of strategyRef x rungs
   * for all gamma strategy legs imn the provided array
   *
   * @param orders
   * @returns
   */
  public static extractGammaRungs(orders: OrderDataImpl[]): Map<string, OrderDataImpl[]> {
    let ret: Map<string, OrderDataImpl[]> = new Map<string, OrderDataImpl[]>();
    for (let order of orders) {
      if (null != order.gammaRungId) {
        if (!ret.has(order.strategyRef)) {
          ret.set(order.strategyRef, []);
        }
        ret.get(order.strategyRef).push(order);
      }
    }
    return ret;
  }

  /**
   * walk the gamma til we find an order with a diff intent to "us"
   * if above - walk down , otherwise walk up
   */
  static getFlipRungOfGamma(order: OrderData, above: boolean): OrderDataImpl {
    let ret: OrderDataImpl = null;
    if (null != order && StrategyType.GAMMA == order.strategyType) {
      let myIntent: OrderIntent = order.clientIntent;
      let allRungs = GammaUtils.getAllRungs(order);
      for (
        let index = above ? 0 : allRungs.length - 1;
        above ? index < allRungs.length : index >= 0;
        above ? index++ : index--
      ) {
        const rung = allRungs[index];
        if (rung.clientIntent != myIntent) {
          ret = rung;
          break;
        }
      }
    }
    return ret;
  }

  /**
   * we want the frist order on "the other side" of this gamma
   * So it may be the mid, if there is one -
   * or esle it may be a change in intent
   * of it may be the edge if they are all the same intent
   *
   * @param allRungs
   * @param relativeTo
   * @returns
   */
  public static getOtherSideOfGamma(allRungs: OrderDataImpl[], relativeTo: OrderData): OrderData {
    let ret = allRungs.find((rung) => rung.orderState == OrderState.GAMMA_MID_PENDING);
    if (ret == undefined) ret = null;

    let above: boolean = null;
    if (null != relativeTo) {
      if (null == ret) {
        if (OrderType.LIMIT_TP == relativeTo.orderType) {
          above = OrderIntent.SELL == relativeTo.clientIntent;
        } else {
          above = OrderIntent.BUY == relativeTo.clientIntent;
        }

        ret = GammaUtils.getFlipRungOfGamma(relativeTo, above);

        if (null == ret) {
          // all same intent - so we regard the first or last rung as the mid based on our intent and type
          ret = above ? allRungs[0] : allRungs[allRungs.length - 1];
        }
      } else {
        above = relativeTo.gammaRungId.lt(ret.gammaRungId);
      }
    }

    return ret;
  }

  public static getMidRungOfGamma(allRungs: OrderDataImpl[]): OrderData {
    let ret = allRungs.find((rung) => rung.orderState == OrderState.GAMMA_MID_PENDING);
    if (ret == undefined) ret = null;

    return ret;
  }

  public static getAllRungs(gamma: OrderDataImpl): Array<OrderDataImpl> {
    let ret: Array<OrderDataImpl> = null;
    const gammaRelationship = gamma.siblingRelationship as SiblingRelationship;

    ret = [...gammaRelationship.siblingOrders];
    ret = ret.sort((a, b) => {
      return a.gammaRungId.sub(b.gammaRungId).toNumber();
    });
    return ret;
  }
  /**
   * Given a set of Orders
   * If any of them are Gamma Rungs
   * See if any of them are no "adjacent to the market"
   * As in if there is a break fo the runsg from the mid
   *
   * This example
   * <pre>
   * Rung 1
   * Rung 2 **
   * Rung 3
   *  Mid 4
   * Rung 5 **
   * Rung 6
   * Rung 7
   * </pre>
   * would be "bad" as rung 2 is seldtced, but not 3 - so there is agap between it and the market
   * In that instamnce the result would be
   * <ul>
   * <li>Rung 2 (Specified)</li>
   * <li>Rung 3 (Filling the gap)</li>
   * <li>Rung 5 (Specified)</li>
   * </ul>
   *
   * @param gammaRungs
   * @returns
   */
  public static getBadGammaQuickFillRungs(
    gammaRungs: Map<string, OrderDataImpl[]>,
  ): Map<string, OrderDataImpl> {
    let ret: Map<string, OrderDataImpl> = new Map<string, OrderDataImpl>();

    if (null != gammaRungs && gammaRungs.size > 0) {
      // we have some gammas
      for (let strategyRef of gammaRungs.keys()) {
        let thisStrategiesRungsForFilling: OrderDataImpl[] = gammaRungs.get(strategyRef);
        let allRungs: OrderDataImpl[] = this.getAllRungs(thisStrategiesRungsForFilling[0]);

        // these are back the front to start with - as we walk em down
        let lowestRung: Long = new Long(allRungs.length);
        let highestRung: Long = new Long(1);
        let selectedGammaRungIds: number[] = [];

        // figure out the higest and lowest selected rungs
        // as we onb,ly care about rungs between these
        thisStrategiesRungsForFilling.forEach((rung) => {
          if (rung.gammaRungId.lt(lowestRung)) {
            lowestRung = rung.gammaRungId;
          }

          if (rung.gammaRungId.gt(highestRung)) {
            highestRung = rung.gammaRungId;
          }

          selectedGammaRungIds.push(rung.gammaRungId.toNumber());
        });

        let temp: Map<string, OrderDataImpl> = new Map<string, OrderDataImpl>();
        let bad: boolean = false;
        let last: OrderData = null;
        let midRung = this.getMidRungOfGamma(allRungs);
        // walk between em - see if any gaps
        allRungs.forEach((rung) => {
          let thisRungsId = rung.gammaRungId;

          // first find "other side" of gamma
          let switchRung = midRung;
          if (null == switchRung) {
            switchRung = GammaUtils.getOtherSideOfGamma(allRungs, rung);
          }
          let switchRungId = switchRung.gammaRungId;

          let isMidRung: boolean = null != midRung && thisRungsId.eq(midRung.gammaRungId);

          // now which side of it am "I"
          let above: boolean = thisRungsId.lt(switchRungId);

          if (
            false == isMidRung &&
            (above ? thisRungsId.gte(lowestRung) : thisRungsId.lte(highestRung))
          ) {
            // check if we have a bad one i.e. _any_ gap
            let isSelected: boolean = selectedGammaRungIds.includes(rung.gammaRungId.toNumber());
            if (!isSelected) {
              bad = true;
            }

            // link em up
            if (null != last) {
              if (above) {
                last["nextClosest"] = rung;
              } else {
                rung["nextClosest"] = last;
              }
            } else {
              last = rung;
            }

            // stash em
            temp.set(rung.orderId.toString(), rung);
          }
        });
        if (bad) {
          temp.forEach((value, key) => {
            ret.set(key, value);
          });
        }
      }
    }
    return ret;
  }

  /**
   * Get the "next cloest to mid" order in the gamma to this one
   * @param order
   * @param allRungs
   */
  static getNextRungClosestToMid(order: OrderData): OrderData {
    let ret: OrderData = null;
    if (null != order && StrategyType.GAMMA == order.strategyType) {
      let allRungs = GammaUtils.getAllRungs(order);
      let midRung = GammaUtils.getMidRungOfGamma(allRungs);
      let hasMid: boolean = null != midRung;
      let isMid = hasMid ? order.gammaRungId.eq(midRung.gammaRungId) : false;

      let above: boolean = false;
      let adjacent: boolean = false;
      if (hasMid) {
        adjacent =
          order.gammaRungId.eq(midRung.gammaRungId.add(1)) ||
          order.gammaRungId.eq(midRung.gammaRungId.sub(1));
        above = order.gammaRungId.lt(midRung.gammaRungId);
      } else {
        // no mid - so next closest to "other side"
        let limit = GammaUtils.getOtherSideOfGamma(allRungs, order);
        adjacent =
          order.gammaRungId.eq(limit.gammaRungId.add(1)) ||
          order.gammaRungId.eq(limit.gammaRungId.sub(1));
        above = order.gammaRungId.lt(limit.gammaRungId);
      }
      if (isMid) {
        // NOP
      } else if (adjacent) {
        // NOP - beside the market
      } else {
        if (above) {
          // want the one below me (allow for zero index)
          ret = allRungs[order.gammaRungId.toNumber() - 1 + 1];
        } else {
          // want the one above me (allow for zero index)
          ret = allRungs[order.gammaRungId.toNumber() - 1 - 1];
        }
      }
    }
    return ret;
  }

  /**
   * Get all of the rungs "outside" of this one
   * so fuirther from mid, on same side
   * @param data
   */
  static getRungsOutside(order: OrderData): Array<OrderData> {
    let ret: Array<OrderData> = null;
    if (null != order && StrategyType.GAMMA == order.strategyType) {
      let allRungs = GammaUtils.getAllRungs(order);
      let midRung = GammaUtils.getMidRungOfGamma(allRungs);
      let hasMid: boolean = null != midRung;
      let isMid = hasMid ? order.gammaRungId.eq(midRung.gammaRungId) : false;
      let above: boolean = false;
      if (hasMid) {
        above = order.gammaRungId.lt(midRung.gammaRungId);
      } else {
        // no mid - so next closest to "other side"
        let limit = GammaUtils.getOtherSideOfGamma(allRungs, order);
        above = order.gammaRungId.lt(limit.gammaRungId);
      }

      if (isMid) {
        // NOP
      } else {
        let temp: Array<OrderData> = [];
        if (above) {
          let startRungId = order.gammaRungId.toNumber() - 1; //allow for zero index
          // want all the ones above me (allow for zero index)
          if (startRungId <= 0) {
            // edge - NOP
          } else {
            for (let index = startRungId - 1; index >= 0; index--) {
              let candidate = allRungs[index];
              if (hasMid && midRung.gammaRungId == candidate.gammaRungId) {
                // NOP
              } else {
                temp.push(candidate);
              }
            }
          }
        } else {
          let startRungId = order.gammaRungId.toNumber() + 1; //allow for zero index
          if (startRungId > allRungs.length) {
            // edge - NOP
          } else {
            for (let index = startRungId - 1; index < allRungs.length; index++) {
              let candidate = allRungs[index];
              if (hasMid && midRung.gammaRungId == candidate.gammaRungId) {
                // NOP
              } else {
                temp.push(candidate);
              }
            }
          }
        }
        if (temp.length > 0) ret = temp;
      }
    }
    return ret;
  }

  /**
   * Determine the RungPiosition of the provided Order
   * If we are also provided with a dataset - then it is the rung position withion that dataset (e.g. orders selected for fill)
   * If we are not  provided with a dataset - then it is the rung position withion that gamma
   * @param order
   * @param dataSet
   * @returns
   */
  static getRungPosition(order: OrderDataImpl, dataSet: OrderDataImpl[] = null): RungPosition {
    let ret: RungPosition = null;

    if (null != order && StrategyType.GAMMA == order.strategyType) {
      let allRungs = GammaUtils.getAllRungs(order);
      let midRung = GammaUtils.getMidRungOfGamma(allRungs);
      let isMid = null == midRung ? false : order.gammaRungId.eq(midRung.gammaRungId);
      let isHighest = order.gammaRungId.eq(1);
      let isLowest = order.gammaRungId.eq(allRungs.length);

      let dataSetLow = null;
      let dataSetHigh = null;

      if (null != dataSet) {
        if (null == dataSetLow || null == dataSetHigh) {
          // we need the highest and lowest rung in the dataset
          // from the same gamma as 'order'
          dataSet.forEach((rung) => {
            if (
              rung.strategyRef == order.strategyRef &&
              (null == dataSetLow || rung.gammaRungId > dataSetLow)
            ) {
              dataSetLow = rung.gammaRungId;
            }
            if (
              rung.strategyRef == order.strategyRef &&
              (null == dataSetHigh || rung.gammaRungId < dataSetHigh)
            ) {
              dataSetHigh = rung.gammaRungId;
            }
          });
        }

        isHighest = order.gammaRungId.eq(dataSetHigh);
        isLowest = order.gammaRungId.eq(dataSetLow);
      }

      if (isMid) {
        ret = RungPosition.MID;
      } else if (isLowest) {
        ret = RungPosition.LOWEST;
      } else if (isHighest) {
        ret = RungPosition.HIGHEST;
      }
    }
    return ret;
  }
}
